import React, { useState, useEffect, useRef } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Check, X, Maximize2, Square, RectangleHorizontal, Smartphone } from "lucide-react";
import { loadImage, cropAndResize, resizeOnly } from "../../lib/image";

/**
 * Single-image crop dialog built on react-image-crop so the crop rectangle is
 * freely draggable + resizable via corner/edge handles — even in "Free" mode.
 *
 * Props:
 *  - file: File (original image)
 *  - onCancel()
 *  - onApply(blob, {width,height})
 */
export default function ImageCropModal({ file, onCancel, onApply }) {
  const [srcUrl, setSrcUrl] = useState(null);
  const [srcDims, setSrcDims] = useState({ w: 0, h: 0 });
  const [aspect, setAspect] = useState(undefined); // undefined = free
  const [crop, setCrop] = useState(null); // percent crop while dragging
  const [completedCrop, setCompletedCrop] = useState(null); // pixel crop on commit
  const [applying, setApplying] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSrcUrl(url);
    (async () => {
      try {
        const img = await loadImage(file);
        setSrcDims({ w: img.naturalWidth, h: img.naturalHeight });
      } catch { /* ignore */ }
    })();
    return () => URL.revokeObjectURL(url);
  }, [file]);

  /** Lay down a sensible starting crop whenever the image loads or aspect changes. */
  const seedCrop = (width, height, nextAspect) => {
    if (nextAspect) {
      const c = centerCrop(
        makeAspectCrop({ unit: "%", width: 80 }, nextAspect, width, height),
        width,
        height,
      );
      setCrop(c);
    } else {
      // Free mode — 80% centered rectangle, no locked aspect.
      setCrop({ unit: "%", x: 10, y: 10, width: 80, height: 80 });
    }
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    seedCrop(width, height, aspect);
  };

  const changeAspect = (nextAspect) => {
    setAspect(nextAspect);
    const el = imgRef.current;
    if (el) seedCrop(el.width, el.height, nextAspect);
  };

  /** Convert the completedCrop (in displayed-image px) to original-image px. */
  const toSourceRect = () => {
    const el = imgRef.current;
    if (!el || !completedCrop || !completedCrop.width || !completedCrop.height) return null;
    const scaleX = el.naturalWidth / el.width;
    const scaleY = el.naturalHeight / el.height;
    return {
      x: Math.round(completedCrop.x * scaleX),
      y: Math.round(completedCrop.y * scaleY),
      width: Math.round(completedCrop.width * scaleX),
      height: Math.round(completedCrop.height * scaleY),
    };
  };

  const applyCrop = async () => {
    if (!file) return;
    const rect = toSourceRect();
    setApplying(true);
    try {
      const blob = await cropAndResize(file, rect);
      const img = await loadImage(blob);
      onApply(blob, { width: img.naturalWidth, height: img.naturalHeight });
    } catch {
      setApplying(false);
    }
  };

  const useFull = async () => {
    if (!file) return;
    setApplying(true);
    try {
      const blob = await resizeOnly(file);
      const img = await loadImage(blob);
      onApply(blob, { width: img.naturalWidth, height: img.naturalHeight });
    } catch {
      setApplying(false);
    }
  };

  const ratios = [
    { id: "free", label: "Free", ratio: undefined, Icon: Maximize2 },
    { id: "1:1", label: "1:1", ratio: 1, Icon: Square },
    { id: "16:9", label: "16:9", ratio: 16 / 9, Icon: RectangleHorizontal },
    { id: "9:16", label: "9:16", ratio: 9 / 16, Icon: Smartphone },
  ];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 fade-in"
      data-testid="crop-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="w-full max-w-3xl rounded-lg border border-white/10 bg-[#0F0F0F] shadow-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <div className="label-eyebrow">crop & resize</div>
            <div className="font-display font-black text-[15px] tracking-tight mt-1">
              Adjust your image
            </div>
          </div>
          <button
            onClick={onCancel}
            className="w-9 h-9 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 focus-ring"
            aria-label="Close"
            data-testid="crop-cancel-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Cropper */}
        <div
          className="relative w-full flex-1 min-h-0 flex items-center justify-center bg-[#0A0A0A] overflow-auto p-4"
          style={{ minHeight: "min(60vh, 480px)" }}
        >
          {srcUrl && (
            <ReactCrop
              crop={crop}
              onChange={(_px, percent) => setCrop(percent)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
              keepSelection
              ruleOfThirds
              data-testid="react-crop"
            >
              <img
                ref={imgRef}
                src={srcUrl}
                alt=""
                onLoad={onImageLoad}
                style={{ maxHeight: "min(60vh, 480px)", maxWidth: "100%", display: "block" }}
              />
            </ReactCrop>
          )}
        </div>

        {/* Controls */}
        <div className="px-5 py-4 border-t border-white/10 space-y-3">
          {/* Aspect chooser */}
          <div className="flex flex-wrap items-center gap-2" data-testid="crop-aspect-row">
            <span className="label-eyebrow mr-2">aspect</span>
            {ratios.map((r) => {
              const active = aspect === r.ratio;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => changeAspect(r.ratio)}
                  className={`btn-ghost focus-ring !h-8 !px-3 ${active ? "!border-white/40 !bg-white/[0.06]" : ""}`}
                  data-testid={`aspect-${r.id}`}
                >
                  <r.Icon className="w-3.5 h-3.5" />
                  <span className="text-[11px] uppercase tracking-[0.15em]">{r.label}</span>
                </button>
              );
            })}
            {srcDims.w > 0 && (
              <span className="ml-auto label-eyebrow opacity-60 font-mono-p">
                source: {srcDims.w}×{srcDims.h}
              </span>
            )}
          </div>

          <div className="label-eyebrow opacity-60">
            Drag the rectangle to position it, pull the corners or edges to resize. Images are downscaled to a max of 1024×1024 before upload.
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="btn-ghost focus-ring"
              data-testid="crop-discard-btn"
              disabled={applying}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={useFull}
              className="btn-ghost focus-ring"
              data-testid="crop-use-full-btn"
              disabled={applying}
            >
              Use full image
            </button>
            <button
              type="button"
              onClick={applyCrop}
              className="btn-pill focus-ring"
              data-testid="crop-apply-btn"
              disabled={applying || !completedCrop?.width || !completedCrop?.height}
            >
              <Check className="w-3.5 h-3.5" />
              Apply crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
